import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { DateTime } from 'luxon';
import React from 'react';
import { CompanyEventDetails } from '@benzinga/calendar-manager';
import { toTitleCase } from '@benzinga/utils';

import { dateFilterParams, formatColumnDate, headerInfoTooltipDef, sanitizeHttpUrl } from '../lib/utils';

const ROWS_KEEP = { newRowsAction: 'keep' as const };
const TEXT_FILTER_PARAMS = { ...ROWS_KEEP, buttons: ['clear'] as const };
const DATE_FILTER_PARAMS = { ...ROWS_KEEP, ...dateFilterParams, buttons: ['clear'], inRangeInclusive: true };

function formatStartTime(raw: string | undefined): string {
  if (!raw?.trim()) {
    return '—';
  }
  const trimmedString = raw.trim();
  const fromHms = DateTime.fromFormat(trimmedString, 'HH:mm:ss');
  if (fromHms.isValid) {
    return fromHms.toFormat('h:mm a');
  }
  const fromIso = DateTime.fromISO(trimmedString);
  if (fromIso.isValid) {
    return fromIso.toFormat('h:mm a');
  }
  return trimmedString;
}

function formatInitialCaps(raw: string | undefined): string {
  if (!raw?.trim()) return '';
  return toTitleCase(raw.trim().toLowerCase());
}

const EmDash = () => <span className="text-gray-500">—</span>;

const ExternalLink = ({ children, href }: { children: React.ReactNode; href: string }) => (
  <a className="calendar-table-link" href={href} rel="noopener noreferrer" target="_blank">
    {children}
  </a>
);

const LinksCell = ({ data }: { data: CompanyEventDetails }) => {
  const sourceUrl = sanitizeHttpUrl(data?.source_link);
  if (!sourceUrl) {
    return <EmDash />;
  }
  return <ExternalLink href={sourceUrl}>Source</ExternalLink>;
};

const JoinWebcastCell = ({ data }: { data: CompanyEventDetails }) => {
  const webcastUrl = sanitizeHttpUrl(data?.webcast_link);
  const phone = data?.phone_number?.trim();

  if (!webcastUrl && !phone) {
    return <EmDash />;
  }

  return (
    <div className="flex flex-col gap-1 text-[13px]">
      {webcastUrl ? <ExternalLink href={webcastUrl}>Join Webcast</ExternalLink> : null}
      {phone ? (
        <a className="calendar-table-link" href={`tel:${phone.replace(/[^\d+]/g, '')}`} rel="noopener noreferrer">
          {phone}
        </a>
      ) : null}
    </div>
  );
};

const TickersCell = ({ data }: { data: CompanyEventDetails }) => {
  if (!data?.securities?.length) {
    return <EmDash />;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {data.securities.map((s, i) => (
        <span key={`${s.symbol}-${i}`}>
          <a
            className="calendar-table-ticker-link"
            href={`/quote/${encodeURIComponent(s.symbol)}/events`}
            rel="noopener noreferrer"
          >
            {s.symbol}
          </a>
          {i < data.securities.length - 1 ? <span className="text-gray-400">, </span> : null}
        </span>
      ))}
    </div>
  );
};

export default [
  {
    autoHeight: true,
    cellClass: 'wrap-cell',
    cellRenderer: TickersCell,
    field: 'securities',
    filter: 'agTextColumnFilter',
    filterParams: TEXT_FILTER_PARAMS,
    headerName: 'Tickers',
    initialWidth: 152,
    minWidth: 96,
    sortable: false,
    valueGetter: ({ data }) => data?.securities?.map(s => s.symbol).join(', ') ?? '',
    ...headerInfoTooltipDef('Click a symbol to open its events on the quote page'),
  },
  {
    cellClass: 'ticker-cell',
    field: 'date_start',
    filter: 'agDateColumnFilter',
    filterParams: DATE_FILTER_PARAMS,
    headerName: 'Date',
    initialWidth: 104,
    minWidth: 92,
    sortIndex: 0,
    valueFormatter: ({ data }) => (data?.date_start ? formatColumnDate(data.date_start) : ''),
    valueGetter: ({ data }) => data?.date_start,
    ...headerInfoTooltipDef('Event start date'),
  },
  {
    field: 'start_time',
    filter: 'agTextColumnFilter',
    filterParams: TEXT_FILTER_PARAMS,
    headerName: 'Time',
    initialWidth: 76,
    minWidth: 68,
    sortable: false,
    valueFormatter: ({ data }) => formatStartTime(data?.start_time),
    valueGetter: ({ data }) => data?.start_time ?? '',
    ...headerInfoTooltipDef('Scheduled start time when provided by the feed'),
  },
  {
    cellClass: 'ticker-cell',
    field: 'date_end',
    filter: 'agDateColumnFilter',
    filterParams: DATE_FILTER_PARAMS,
    headerName: 'End date',
    initialWidth: 100,
    minWidth: 88,
    sortable: false,
    valueFormatter: ({ data }) => {
      if (!data?.date_end || data.date_end === data.date_start) {
        return '';
      }
      return formatColumnDate(data.date_end);
    },
    valueGetter: ({ data }) => (data?.date_end && data.date_end !== data.date_start ? data.date_end : ''),
    ...headerInfoTooltipDef('End date for multi-day events (blank when same as start date)'),
  },
  {
    autoHeight: true,
    cellClass: 'wrap-cell',
    field: 'event_name',
    filter: 'agTextColumnFilter',
    filterParams: TEXT_FILTER_PARAMS,
    flex: 1,
    headerName: 'Event',
    initialWidth: 200,
    minWidth: 120,
    valueFormatter: ({ data }) => formatInitialCaps(data?.event_name),
  },
  {
    autoHeight: true,
    cellClass: 'wrap-cell',
    field: 'event_type',
    filter: 'agSetColumnFilter',
    filterParams: TEXT_FILTER_PARAMS,
    headerName: 'Type',
    initialWidth: 128,
    minWidth: 88,
    valueFormatter: ({ data }) => formatInitialCaps(data?.event_type),
  },
  {
    cellClass: 'ticker-cell',
    field: 'importance',
    filter: 'agNumberColumnFilter',
    filterParams: TEXT_FILTER_PARAMS,
    headerName: 'Importance',
    initialWidth: 90,
    minWidth: 72,
    type: 'numericColumn',
    valueFormatter: ({ data }) => (data?.importance != null && data.importance !== 0 ? String(data.importance) : ''),
    ...headerInfoTooltipDef('Higher values indicate greater significance (API scale)'),
  },
  {
    autoHeight: true,
    cellClass: 'wrap-cell',
    field: 'location',
    filter: 'agTextColumnFilter',
    filterParams: TEXT_FILTER_PARAMS,
    headerName: 'Location',
    initialWidth: 136,
    minWidth: 96,
    valueFormatter: ({ data }) => (data?.location?.trim() ? data.location.trim() : ''),
  },
  {
    autoHeight: true,
    cellClass: 'wrap-cell',
    cellRenderer: JoinWebcastCell,
    field: 'webcast_link',
    filter: 'agTextColumnFilter',
    filterParams: TEXT_FILTER_PARAMS,
    headerName: 'Dial-in',
    initialWidth: 112,
    minWidth: 88,
    sortable: false,
    valueGetter: ({ data }) => [data?.webcast_link, data?.phone_number].filter(Boolean).join(' ') || '',
    ...headerInfoTooltipDef('Webcast and conference dial-in when available'),
  },
  {
    autoHeight: true,
    cellClass: 'wrap-cell',
    cellRenderer: LinksCell,
    field: 'source_link',
    filter: 'agTextColumnFilter',
    filterParams: TEXT_FILTER_PARAMS,
    headerName: 'Links',
    initialWidth: 84,
    minWidth: 72,
    sortable: false,
    valueGetter: ({ data }) => data?.source_link ?? '',
    ...headerInfoTooltipDef('Press or source link when provided'),
  },
] as (ColDef | ColGroupDef)[];
